import { TEST_HUB_URL } from '@/constants/constants';
import { apiFetch } from '@/utils/apiWrapper';

// Base URL for extensions endpoints
const extensionsUrl = `${TEST_HUB_URL}subscriptions/extension`;

// Types for Extension data
export type Extension = {
  _id: string;
  label: string;
  category: string;
  price: string;
  guid: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  __v: number;
};
// Create extension DTO
export interface CreateExtensionDTO {
  label: string;
  category: string;
  price: string;
  guid?: string;
}

// Update extension DTO
export interface UpdateExtensionDTO {
  label?: string;
  category?: string;
  price?: string;
}

// Extension filters
export interface ExtensionFilters {
  category?: string;
}

/**
 * Fetches all extensions with optional filters
 * @param filters Optional filters for the extensions
 * @returns Array of extensions
 */
export async function getAllExtensions(
  organizationId: string,
  filters?: ExtensionFilters,
): Promise<Extension[]> {
  try {
    let url = `${extensionsUrl}/${organizationId}`;

    // Add query parameters if filters provided
    if (filters) {
      const queryParams = new URLSearchParams();

      if (filters.category) {
        queryParams.append('category', filters.category);
      }

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const response = await apiFetch(url);

    if (!response) {
      throw new Error('Error fetching extensions');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching extensions:', error);
    throw error;
  }
}

/**
 * Fetches an extension by ID
 * @param id Extension ID
 * @returns Extension data
 */
export async function getExtensionById(id: string): Promise<Extension> {
  try {
    const url = `${extensionsUrl}${id}`;

    const response = await apiFetch(url);

    if (!response) {
      throw new Error(`Could not fetch extension with ID: ${id}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching extension with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Creates a new extension
 * @param extensionData Extension data to create
 * @returns The created extension
 */
export async function createExtension(extensionData: CreateExtensionDTO): Promise<Extension> {
  try {
    const response = await apiFetch(extensionsUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(extensionData),
    });

    if (!response) {
      throw new Error('Error creating extension');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating extension:', error);
    throw error;
  }
}

/**
 * Updates an extension
 * @param id Extension ID to update
 * @param extensionData Updated extension data
 * @returns The updated extension
 */
export async function updateExtension(
  id: string,
  extensionData: UpdateExtensionDTO,
): Promise<Extension> {
  try {
    const url = `${extensionsUrl}${id}`;

    const response = await apiFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(extensionData),
    });

    if (!response) {
      throw new Error(`Could not update extension with ID: ${id}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error updating extension with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Deletes an extension
 * @param id Extension ID to delete
 * @returns Success message
 */
export async function deleteExtension(id: string): Promise<{ success: boolean; message: string }> {
  try {
    const url = `${extensionsUrl}${id}`;

    const response = await apiFetch(url, {
      method: 'DELETE',
    });

    if (!response) {
      throw new Error(`Could not delete extension with ID: ${id}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error deleting extension with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Fetches extensions by category
 * @param category Category name
 * @returns Array of extensions
 */
export async function getExtensionsByCategory(
  organizationId: string,
  category: string,
): Promise<Extension[]> {
  return getAllExtensions(organizationId, { category });
}
